import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bed, BookOpen, Briefcase, GraduationCap, HandHeart, Home, KeyRound, Landmark, Sun, UserCheck } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";
import { useNarrationCue } from "@/components/clientDemo/tour/TourCuePointer";
import { CountUp } from "@/components/clientDemo/intro/IntroStats";

const RHYTHM = [["7:00 AM", "Personal space & care", Sun], ["8:00 AM", "Devotion", BookOpen], ["Day", "Work on self · Employment", Briefcase], ["Evening", "Life skills & certifications", UserCheck]];
const MILESTONES = [
  { phrase: "ninety days", when: "90 days", title: "First bank account", detail: "Opens a bank account and begins financial literacy.", Icon: Landmark },
  { phrase: "one hundred eighty days", when: "180 days", title: "Self-managed", detail: "Managing himself, his schedule and his money with proficiency.", Icon: UserCheck },
  { phrase: "twelve months", when: "12 months", title: "His own room", detail: "Moves out of shared space and into a private room.", Icon: Bed },
  { phrase: "eighteen months", when: "18 months", title: "His own apartment", detail: "Pays his own bills, governed by the Alliance with very little oversight.", Icon: KeyRound },
  { phrase: "twenty-four months", when: "24 months", title: "Graduated", detail: "Fully on his own, everything in his control, with a family that keeps supporting him.", Icon: GraduationCap },
];
export const JOURNEY_CUES = [
  { phrase: "Every day has structure", target: "exchange-journey-rhythm" },
  ...MILESTONES.map((m, i) => ({ phrase: m.phrase, target: `exchange-journey-node-${i}` })),
  { phrase: "higher level of care", target: "exchange-journey-care" },
];

function Rhythm() {
  return (
    <div data-testid="exchange-journey-rhythm" className="rounded-2xl border border-[#D4AF37]/25 bg-[#120C1C]/80 p-4">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-2">Every day has structure · Marcus T.</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {RHYTHM.map(([t, label, Icon]) => (
          <div key={label} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
            <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#F4D88A]"><Icon className="w-3.5 h-3.5" />{t}</div>
            <div className="text-xs text-white mt-0.5">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Timeline({ at }) {
  const m = MILESTONES[Math.max(0, at)];
  return (
    <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#120C1C]/80 p-4">
      <div className="relative grid grid-cols-5 gap-1">
        <div className="absolute left-[10%] right-[10%] top-5 h-0.5 bg-white/10" />
        <motion.div className="absolute left-[10%] top-5 h-0.5 bg-gradient-to-r from-[#C9A227] to-[#F4D88A]" animate={{ width: `${(Math.max(0, at) / 4) * 80}%` }} transition={{ duration: 0.8 }} />
        {MILESTONES.map((x, i) => (
          <div key={x.when} data-testid={`exchange-journey-node-${i}`} className="relative flex flex-col items-center text-center">
            <motion.div animate={{ scale: at === i ? 1.15 : 1 }}
              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center bg-[#0A0612] ${at >= i ? "border-[#D4AF37] text-[#F4D88A]" : "border-white/15 text-slate-600"}`}>
              <x.Icon className="w-4 h-4" />
            </motion.div>
            <div className={`mt-1.5 font-mono text-[10px] ${at >= i ? "text-[#F4D88A]" : "text-slate-500"}`}>{x.when}</div>
            <div className="text-[11px] text-slate-300 leading-tight">{x.title}</div>
          </div>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.p key={m.when} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          data-testid="exchange-journey-detail" className="mt-4 text-sm text-slate-200">
          <span className="font-mono text-[#F4D88A] mr-2">{m.when}</span>{m.detail}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

function CareAndOutcomes({ lit }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
      <div data-testid="exchange-journey-care" className={`rounded-2xl border p-4 bg-[#120C1C]/80 transition-colors ${lit ? "border-teal-400/60" : "border-white/10"}`}>
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-teal-300"><HandHeart className="w-4 h-4" /> Not every path is the same</div>
        <p className="mt-2 text-sm text-slate-200"><span className="text-white font-semibold">Dana R.</span> · assessed and referred to a partner residential treatment program. Records transferred securely, bed returned to the waitlist.</p>
        <span className="mt-2 inline-block px-2 py-0.5 rounded-full bg-teal-500/15 border border-teal-400/40 text-teal-200 font-mono text-[10px] tracking-[0.14em] uppercase">Referred · higher level of care</span>
      </div>
      <div className="flex md:flex-col gap-3">
        {[["Graduated to independence", 41, Home], ["Referred to higher care", 9, HandHeart]].map(([label, to, Icon]) => (
          <div key={label} data-testid={`exchange-journey-outcome-${to}`} className="flex-1 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] px-4 py-2">
            <div className="flex items-center gap-2 font-mono text-2xl font-bold text-[#F4D88A]"><Icon className="w-5 h-5" /><CountUp to={to} duration={1400} /></div>
            <div className="text-[10px] text-slate-400">{label} · sample</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Beat 07 — one resident from daily structure to graduation, plus an honest higher-care exit.
export default function ExchangeJourneySection() {
  const { isActive, current } = useGuidedTour();
  const live = isActive && current?.id === "beat07";
  const cue = useNarrationCue(JOURNEY_CUES.map((c) => c.phrase), live);
  const at = live ? Math.min(4, cue - 1) : 4;
  return (
    <section data-testid="exchange-journey-section" className="mt-8 rounded-3xl border border-[#D4AF37]/20 bg-[#0A0612] p-6 md:p-8">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">// 10 · Streets to Graduation</div>
        <h2 className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight text-white">A Resident&apos;s Journey</h2>
      </div>
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          <Rhythm />
          <Timeline at={at} />
          <CareAndOutcomes lit={live && cue === 6} />
        </div>
        <aside data-testid="exchange-journey-dock" className="hidden xl:flex w-[440px] shrink-0 rounded-2xl border border-dashed border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] p-6 flex-col justify-center">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A227]">24 months · one life</div>
          <p className="mt-3 text-sm italic text-slate-300">Every outcome is tracked. Every life is accounted for.</p>
        </aside>
      </div>
    </section>
  );
}
